import { gql, ApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';
import { apolloClient } from '../apollo/client';
import type { Equipe, FaseGrupoPartidaEquipe } from '../types/graphql';

const baseErrorMessage = 'N\u00e3o foi poss\u00edvel completar a opera\u00e7\u00e3o';

function normalizeGraphqlError(error: unknown): never {
  if (error instanceof ApolloError) {
    const backendMessage = error.graphQLErrors?.[0]?.message ?? error.message ?? 'erro desconhecido.';
    throw new Error(`${baseErrorMessage}: ${backendMessage}`);
  }

  if (error instanceof GraphQLError) {
    throw new Error(`${baseErrorMessage}: ${error.message ?? 'erro desconhecido.'}`);
  }

  if (error instanceof Error) {
    throw error;
  }

  throw new Error(`${baseErrorMessage}: erro desconhecido.`);
}

export const VINCULAR_EQUIPE_AO_GRUPO = gql`
  mutation VincularEquipeAoGrupo($input: VincularEquipeAoGrupoInput!) {
    vincularEquipeAoGrupo(input: $input) {
      id
      criadoEm
      atualizadoEm
      equipe {
        id
        nome
      }
      faseGrupoPartidas {
        id
        nome
      }
      faseCategoriaTorneio {
        id
        nome
      }
    }
  }
`;

export const DESVINCULAR_EQUIPE_DO_GRUPO = gql`
  mutation DesvincularEquipeDoGrupo($input: DesvincularEquipeDoGrupoInput!) {
    desvincularEquipeDoGrupo(input: $input)
  }
`;

export const EQUIPES_DO_GRUPO = gql`
  query EquipesDoGrupo($faseGrupoPartidasId: ID!) {
    equipesDoGrupo(faseGrupoPartidasId: $faseGrupoPartidasId) {
      id
      criadoEm
      atualizadoEm
      equipe {
        id
        nome
        cidade
        vitoriasGrupo 
        derrotasGrupo
        atletas {
          id
          usuario {
            id
            nome
            imagemUrl
            username
          }
        }
      }
      faseGrupoPartidas {
        id
        nome
      }
      faseCategoriaTorneio {
        id
        nome
      }
    }
  }
`;

export const GRUPOS_DA_EQUIPE_NA_FASE = gql`
  query GruposDaEquipeNaFase($faseCategoriaTorneioId: ID!, $equipeId: ID!) {
    gruposDaEquipeNaFase(faseCategoriaTorneioId: $faseCategoriaTorneioId, equipeId: $equipeId) {
      id
      faseGrupoPartidas {
        id
        nome
      }
      faseCategoriaTorneio {
        id
        nome
      }
      equipe {
        id
        nome
      }
    }
  }
`;

export const FASE_GRUPO_PARTIDAS_POR_FASE_CATEGORIA_TORNEIO = gql`
  query FaseGrupoPartidasPorFaseCategoriaTorneio($faseCategoriaTorneioId: ID!) {
    faseGrupoPartidasPorFaseCategoriaTorneio(faseCategoriaTorneioId: $faseCategoriaTorneioId) {
      id
      nome
      equipesCount
    }
  }
`;

type VincularEquipeAoGrupoInput = {
  faseGrupoPartidasId: string;
  equipeId: string;
};

type VincularEquipeAoGrupoData = {
  vincularEquipeAoGrupo: FaseGrupoPartidaEquipe;
};

type DesvincularEquipeDoGrupoData = {
  desvincularEquipeDoGrupo: boolean;
};

type DesvincularEquipeDoGrupoVars = {
  input: { id: string };
};

type EquipesDoGrupoData = {
  equipesDoGrupo: FaseGrupoPartidaEquipe[];
};

type EquipesDoGrupoVars = {
  faseGrupoPartidasId: string;
};

type GruposDaEquipeNaFaseData = {
  gruposDaEquipeNaFase: FaseGrupoPartidaEquipe[];
};

type GruposDaEquipeNaFaseVars = {
  faseCategoriaTorneioId: string;
  equipeId: string;
};

export async function vincularEquipeAoGrupo(params: {
  faseGrupoPartidasId: string;
  equipeId: string;
}): Promise<FaseGrupoPartidaEquipe> {
  try {
    const { faseGrupoPartidasId, equipeId } = params;
    const { data } = await apolloClient.mutate<
      VincularEquipeAoGrupoData,
      { input: VincularEquipeAoGrupoInput }
    >({
      mutation: VINCULAR_EQUIPE_AO_GRUPO,
      variables: { input: { faseGrupoPartidasId, equipeId } },
    });

    if (!data?.vincularEquipeAoGrupo) {
      throw new Error(`${baseErrorMessage}: resposta inv\u00e1lida do servidor.`);
    }

    return data.vincularEquipeAoGrupo;
  } catch (error) {
    normalizeGraphqlError(error);
  }
}

export async function listarEquipesDoGrupo(
  faseGrupoPartidasId: string,
): Promise<FaseGrupoPartidaEquipe[]> {
  try {
    const { data } = await apolloClient.query<EquipesDoGrupoData, EquipesDoGrupoVars>({
      query: EQUIPES_DO_GRUPO,
      variables: { faseGrupoPartidasId },
      fetchPolicy: 'network-only',
    });

    return data?.equipesDoGrupo ?? [];
  } catch (error) {
    normalizeGraphqlError(error);
  }
}

export async function listarGruposDaEquipeNaFase(params: {
  faseCategoriaTorneioId: string;
  equipeId: string;
}): Promise<FaseGrupoPartidaEquipe[]> {
  try {
    const { faseCategoriaTorneioId, equipeId } = params;
    const { data } = await apolloClient.query<GruposDaEquipeNaFaseData, GruposDaEquipeNaFaseVars>({
      query: GRUPOS_DA_EQUIPE_NA_FASE,
      variables: { faseCategoriaTorneioId, equipeId },
      fetchPolicy: 'network-only',
    });

    return data?.gruposDaEquipeNaFase ?? [];
  } catch (error) {
    normalizeGraphqlError(error);
  }
}

export async function transferirEquipeParaOutroGrupo(params: {
  faseCategoriaTorneioId: string;
  equipeId: string;
  novoFaseGrupoPartidasId: string;
}): Promise<FaseGrupoPartidaEquipe> {
  const { faseCategoriaTorneioId, equipeId, novoFaseGrupoPartidasId } = params;

  try {
    const vinculosAtuais = await listarGruposDaEquipeNaFase({ faseCategoriaTorneioId, equipeId });
    const vinculoAtual = vinculosAtuais.find((vinculo) => Boolean(vinculo));

    if (!vinculoAtual) {
      return vincularEquipeAoGrupo({ faseGrupoPartidasId: novoFaseGrupoPartidasId, equipeId });
    }

    if (vinculoAtual.faseGrupoPartidas?.id === novoFaseGrupoPartidasId) {
      return vinculoAtual;
    }

    const desvinculado = await desvincularEquipeDoGrupo(vinculoAtual.id);
    if (!desvinculado) {
      throw new Error(`${baseErrorMessage}: n\u00e3o foi poss\u00edvel remover o v\u00ednculo atual.`);
    }

    return vincularEquipeAoGrupo({ faseGrupoPartidasId: novoFaseGrupoPartidasId, equipeId });
  } catch (error) {
    normalizeGraphqlError(error);
  }
}

export async function desvincularEquipeDoGrupo(vinculoId: string): Promise<boolean> {
  try {
    const { data } = await apolloClient.mutate<
      DesvincularEquipeDoGrupoData,
      DesvincularEquipeDoGrupoVars
    >({
      mutation: DESVINCULAR_EQUIPE_DO_GRUPO,
      variables: { input: { id: vinculoId } },
    });

    return Boolean(data?.desvincularEquipeDoGrupo);
  } catch (error) {
    normalizeGraphqlError(error);
  }
}

export function mapVinculosParaEquipes(vinculos: FaseGrupoPartidaEquipe[]): Equipe[] {
  return vinculos.map((vinculo) => vinculo.equipe).filter(Boolean) as Equipe[];
}

